import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Package, Download, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function MyOrders() {
  const { user, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      api.get("/me/orders").then(({ data }) => setOrders(data)).catch(() => setOrders([])).finally(() => setLoading(false));
    }
  }, [user]);

  if (authLoading) return <div className="text-center py-24 text-[#8a8076]">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: "/account/orders" }} replace/>;

  return (
    <div data-testid="my-orders-page" className="max-w-5xl mx-auto px-6 py-12 sm:py-16">
      <div className="font-script text-3xl text-[#e8478e]">Your Stash</div>
      <h1 className="font-display text-5xl font-black text-[#1c1815] tracking-tight">My Orders</h1>
      <p className="mt-2 text-[#4a4239]">All your past orders — including instant download links.</p>

      {loading && <div className="mt-10 text-[#8a8076] text-center py-12">Loading orders…</div>}

      {!loading && orders.length === 0 && (
        <div className="mt-12 p-12 rounded-3xl border border-[#e5dccd] bg-white text-center shadow-sm">
          <Package className="mx-auto text-[#8a8076]" size={40}/>
          <div className="mt-4 font-script text-2xl text-[#e8478e]">No Orders Yet</div>
          <p className="text-[#4a4239] mt-2">When you place your first order, it'll show up here.</p>
          <Link to="/shop" className="mt-6 inline-block bg-[#2eb6a8] hover:bg-[#1f8d83] text-white font-bold uppercase tracking-[0.2em] text-xs px-6 py-3.5 rounded-full">Shop Now</Link>
        </div>
      )}

      <div className="mt-10 space-y-4">
        {orders.map((o) => {
          const digitals = o.items.filter((i) => i.is_digital);
          return (
            <div key={o.id} data-testid={`order-row-${o.id}`} className="p-5 rounded-3xl border border-[#e5dccd] bg-white hover:border-[#2eb6a8] hover:shadow-md transition-all">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">Order</div>
                  <div className="font-mono text-sm text-[#1c1815]">#{o.id.slice(0, 8).toUpperCase()}</div>
                  <div className="text-xs text-[#8a8076] mt-1">{new Date(o.created_at).toLocaleString()}</div>
                </div>
                <div className="text-right">
                  <div className="font-display font-extrabold text-2xl text-[#1c1815]">${o.total.toFixed(2)}</div>
                  <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1f8d83] bg-[#2eb6a8]/12 border border-[#2eb6a8]/30 px-2 py-1 rounded-full mt-1 inline-block">{o.status}</span>
                </div>
              </div>

              <div className="mt-4 flex items-center gap-3 overflow-x-auto no-scrollbar">
                {o.items.map((it) => (
                  <img key={it.product_id + it.title} src={it.image_url} alt="" className="w-14 h-14 object-cover rounded-xl flex-shrink-0"/>
                ))}
                <div className="text-xs text-[#8a8076] flex-shrink-0 ml-2">
                  {o.items.length} item{o.items.length === 1 ? "" : "s"}
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
                {digitals.length > 0 && (
                  <div className="text-xs text-[#1f8d83] inline-flex items-center gap-1.5 font-bold uppercase tracking-[0.2em]">
                    <Download size={12}/> {digitals.length} digital download{digitals.length === 1 ? "" : "s"}
                  </div>
                )}
                <Link to={`/order/${o.id}`} className="ml-auto text-sm font-bold uppercase tracking-[0.2em] text-[#e8478e] hover:text-[#c93875] inline-flex items-center gap-1">
                  View order <ChevronRight size={14}/>
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
